import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Plus, Upload, Sparkles, Phone, Mail, Globe, MapPin, Trash2, RefreshCw, Flame, CheckCircle2,
  Circle, User, ExternalLink, Star, ChevronDown, ChevronUp, Eye, Building2, EyeOff, Clock,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AREAS } from "@/data/areas";
import LeadSweep from "@/components/crm/LeadSweep";
import { Pill, PotentialPill, btnGhost, btnPrimary, card, field, label, select, selectBase, streetViewUrl } from "@/components/crm/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/crm`;

const STATUSES = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "meeting", label: "Meeting" },
  { key: "proposal", label: "Proposal" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];
const statusLabel = (k) => STATUSES.find((s) => s.key === k)?.label || k;

const PRIORITY_STYLE = {
  A: "border-emerald-400/50 text-emerald-300",
  B: "border-amber-400/50 text-amber-300",
  C: "border-slate-500/50 text-slate-400",
};
const BUILDING_STYLES = [
  { key: "tower", label: "Tower", hint: "7+ floors" },
  { key: "midrise", label: "Mid-rise", hint: "4-6 floors" },
  { key: "garden", label: "Garden", hint: "1-3 floors" },
];
const SITE_CHECKS = [
  { key: "breakroom", label: "Has a breakroom or lobby spot" },
  { key: "no_food_nearby", label: "No food or cafeteria nearby" },
  { key: "has_vendor", label: "Already has a vending company" },
  { key: "decision_maker", label: "Met the decision-maker" },
];
const HIDE_REASONS = [
  { key: "low_quality", label: "Poor location" },
  { key: "rejected", label: "Turned us down" },
];
const URGENCY_STYLE = { high: "text-red-300", medium: "text-amber-300", low: "text-slate-400" };

const LAST_SEARCH_KEY = "vortex_crm_last_search";
const readLastSearch = () => {
  try { return JSON.parse(localStorage.getItem(LAST_SEARCH_KEY)) || null; } catch { return null; }
};
const writeLastSearch = (value) => {
  try { localStorage.setItem(LAST_SEARCH_KEY, JSON.stringify(value)); } catch { /* storage full or blocked */ }
};

const EMPTY_LEAD = { company: "", contact_name: "", title: "", email: "", phone: "", website: "", address: "", city: "", state: "PA", postal_code: "", category: "", notes: "" };

const CrmPanel = ({ token, onAuthError, initialLeadId }) => {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [providers, setProviders] = useState(null);
  const [summary, setSummary] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: "", category: "", q: "", sort: "potential", hidden: "" });
  const [selected, setSelected] = useState(null);

  const [search, setSearch] = useState({ source: "", category: "office", location: "Exton, PA", query: "", radius_miles: 10, open_24h: false, no_food_miles: 1 });
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [picked, setPicked] = useState({});
  const [cursor, setCursor] = useState(null);
  const [searchedAround, setSearchedAround] = useState("");
  const [hiddenSkipped, setHiddenSkipped] = useState(0);
  const [foodSkipped, setFoodSkipped] = useState(null); // {not_24h, food_nearby} from a 24/7 search
  const [openResult, setOpenResult] = useState(null);
  const [details, setDetails] = useState({});
  const [lastSearch, setLastSearch] = useState(() => readLastSearch());

  const [addOpen, setAddOpen] = useState(false);
  const [newLead, setNewLead] = useState(EMPTY_LEAD);
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [busy, setBusy] = useState("");

  const fail = useCallback((err, fallback) => {
    const d = err.response?.data?.detail;
    if (err.response?.status === 401) onAuthError(err);
    else toast.error(typeof d === "string" ? d : fallback);
  }, [onAuthError]);

  const categories = providers?.categories || [];
  const catLabel = (k) => categories.find((c) => c.key === k)?.label || k;

  const loadLeads = useCallback(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    Promise.all([
      axios.get(`${API}/leads`, { ...auth, params }),
      axios.get(`${API}/summary`, auth),
    ])
      .then(([l, s]) => { setLeads(l.data); setSummary(s.data); })
      .catch((err) => fail(err, "Could not load leads."))
      .finally(() => setLoading(false));
  }, [auth, filters, fail]);

  useEffect(() => {
    axios.get(`${API}/providers`, auth)
      .then((res) => {
        setProviders(res.data);
        const firstOn = res.data.sources.find((s) => s.enabled);
        setSearch((s) => ({ ...s, source: s.source || firstOn?.key || "" }));
      })
      .catch((err) => fail(err, "Could not load CRM settings."));
  }, [auth, fail]);

  useEffect(() => {
    const t = setTimeout(loadLeads, filters.q ? 300 : 0);
    return () => clearTimeout(t);
  }, [loadLeads, filters.q]);

  const replaceLead = (lead) => {
    setLeads((prev) => prev.map((l) => (l.id === lead.id ? lead : l)));
    setSelected((s) => (s && s.id === lead.id ? lead : s));
  };

  const openLead = useCallback((id) => {
    axios.get(`${API}/leads/${id}`, auth).then((res) => setSelected(res.data)).catch((err) => fail(err, "Could not open lead."));
  }, [auth, fail]);

  useEffect(() => { if (initialLeadId) openLead(initialLeadId); }, [initialLeadId, openLead]);

  // ---- search ----
  // Results are keyed by source_ref so "Load more" can append without duplicates.
  const showResults = (list, nextCursor, around, append) => {
    const merged = append ? [...results, ...list.filter((r) => !results.some((x) => x.source_ref === r.source_ref))] : list;
    setResults(merged);
    setCursor(nextCursor);
    setSearchedAround(around);
    setPicked((prev) => {
      const next = append ? { ...prev } : {};
      merged.forEach((r) => { if (!(r.source_ref in next)) next[r.source_ref] = false; });
      return next;
    });
    return merged;
  };

  const runSearch = async (e, more = false) => {
    e?.preventDefault();
    setSearching(true);
    if (!more) { setResults(null); setOpenResult(null); }
    try {
      const body = more ? { source: search.source, cursor } : { ...search, category: search.category || null };
      const res = await axios.post(`${API}/search`, body, auth);
      const around = res.data.center?.label || search.location;
      const merged = showResults(res.data.results, res.data.cursor, around, more);
      setHiddenSkipped((n) => (more ? n : 0) + (res.data.hidden || 0));
      setFoodSkipped((prev) => (res.data.food_nearby == null ? null : {
        not_24h: (more && prev ? prev.not_24h : 0) + res.data.not_24h,
        food_nearby: (more && prev ? prev.food_nearby : 0) + res.data.food_nearby,
      }));
      if (!more && !merged.length) {
        toast(!search.open_24h ? "No businesses found. Try a bigger radius or a different type."
          : res.data.cursor ? "No 24/7 places without food nearby on this page. Use Load more to keep looking."
          : "No 24/7 places without food nearby. Try a bigger radius.");
      }
      if (more && !res.data.results.length) toast("No more businesses found.");
      // Keep the last results so reopening the page doesn't cost another paid search
      const saved = { search, results: merged, cursor: res.data.cursor, around, at: new Date().toISOString() };
      setLastSearch(saved);
      writeLastSearch(saved);
    } catch (err) {
      fail(err, "Search failed.");
    } finally {
      setSearching(false);
    }
  };

  const restoreLastSearch = () => {
    if (!lastSearch) return;
    setSearch(lastSearch.search);
    setResults(null);
    setOpenResult(null);
    setTimeout(() => showResults(lastSearch.results, lastSearch.cursor, lastSearch.around, false), 0);
  };

  const toggleResult = async (r) => {
    if (openResult === r.source_ref) { setOpenResult(null); return; }
    setOpenResult(r.source_ref);
    if (details[r.source_ref]) return;
    setDetails((d) => ({ ...d, [r.source_ref]: { loading: true } }));
    try {
      const res = await axios.post(`${API}/search/details`, { source: r.source, source_ref: r.source_ref, website: r.website, category: r.category || null }, auth);
      setDetails((d) => ({ ...d, [r.source_ref]: res.data }));
    } catch (err) {
      setDetails((d) => ({ ...d, [r.source_ref]: { errors: ["Couldn't load details."] } }));
      fail(err, "Couldn't load details.");
    }
  };

  // What gets saved: the search result plus anything the details lookup found
  const withDetails = (r) => {
    const d = details[r.source_ref] || {};
    const dm = d.people?.find((p) => p.decision_maker);
    return { ...r, email: dm?.email || r.email || d.emails?.[0] || "", phone: r.phone || d.phone || d.phones?.[0] || "",
      contact_name: dm?.name || "", title: dm?.title || "", contact_phone: dm?.phone || "",
      website: r.website || d.website || "", notes: d.summary || "", reviews: r.reviews ?? d.reviews ?? null,
      hours_per_week: d.hours_per_week ?? null, style_hint: d.style_hint || "" };
  };

  const importOne = async (r) => {
    setBusy(`import-${r.source_ref}`);
    try {
      const res = await axios.post(`${API}/import`, { leads: [withDetails(r)] }, auth);
      const id = res.data.ids[0];
      setResults((list) => list.map((x) => (x.source_ref === r.source_ref ? { ...x, existing_id: id || "dup" } : x)));
      toast.success(id ? `${r.company} added to your leads.` : `${r.company} is already in your leads.`);
      loadLeads();
    } catch (err) {
      fail(err, "Could not add lead.");
    } finally {
      setBusy("");
    }
  };

  const hideResult = async (r, reason) => {
    setBusy(`hide-${r.source_ref}`);
    try {
      await axios.post(`${API}/search/hide`, { lead: withDetails(r), reason }, auth);
      const rest = results.filter((x) => x.source_ref !== r.source_ref);
      setResults(rest);
      setHiddenSkipped((n) => n + 1);
      if (lastSearch) {
        const saved = { ...lastSearch, results: lastSearch.results.filter((x) => x.source_ref !== r.source_ref) };
        setLastSearch(saved);
        writeLastSearch(saved);
      }
      toast.success(`${r.company} hidden. It won't show in searches again.`);
      if (r.existing_id) loadLeads();
    } catch (err) {
      fail(err, "Could not hide.");
    } finally {
      setBusy("");
    }
  };

  const importPicked = async () => {
    const chosen = results.filter((r) => picked[r.source_ref] && !r.existing_id).map(withDetails);
    if (!chosen.length) return;
    setBusy("import");
    try {
      const res = await axios.post(`${API}/import`, { leads: chosen }, auth);
      toast.success(`Added ${res.data.added} lead${res.data.added === 1 ? "" : "s"}${res.data.skipped_duplicates ? `, skipped ${res.data.skipped_duplicates} already in the CRM` : ""}.`);
      const ids = new Set(chosen.map((r) => r.source_ref));
      setResults((list) => list.map((x) => (ids.has(x.source_ref) ? { ...x, existing_id: x.existing_id || "added" } : x)));
      setPicked({});
      loadLeads();
    } catch (err) {
      fail(err, "Could not add leads.");
    } finally {
      setBusy("");
    }
  };

  // ---- manual add / csv ----
  const addLead = async (e) => {
    e.preventDefault();
    setBusy("add");
    try {
      const body = { ...newLead, category: newLead.category || null };
      const res = await axios.post(`${API}/leads`, body, auth);
      toast.success(`${res.data.company} added.`);
      setAddOpen(false);
      setNewLead(EMPTY_LEAD);
      loadLeads();
      setSelected(res.data);
    } catch (err) {
      fail(err, "Could not add lead.");
    } finally {
      setBusy("");
    }
  };

  const importCsv = async () => {
    setBusy("csv");
    try {
      const res = await axios.post(`${API}/import-csv`, { csv: csvText }, auth);
      toast.success(`Imported ${res.data.added}. Skipped ${res.data.skipped_duplicates} duplicates and ${res.data.rows_without_company} rows with no company.`);
      setCsvOpen(false);
      setCsvText("");
      loadLeads();
    } catch (err) {
      fail(err, "Import failed.");
    } finally {
      setBusy("");
    }
  };

  const readCsvFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    file.text().then(setCsvText);
  };

  // ---- lead actions ----
  const patchLead = async (id, patch) => {
    try {
      const res = await axios.patch(`${API}/leads/${id}`, patch, auth);
      replaceLead(res.data);
      if ("hidden" in patch) {
        toast.success(patch.hidden ? `${res.data.company} hidden. It won't show in your leads or searches.` : `${res.data.company} is back in your leads.`);
        setSelected(null);
        loadLeads();
      }
      return res.data;
    } catch (err) {
      fail(err, "Could not save.");
      return null;
    }
  };

  const enrich = async (lead, providers) => {
    setBusy(`enrich-${lead.id}`);
    try {
      const res = await axios.post(`${API}/leads/${lead.id}/enrich`, providers ? { providers } : {}, auth);
      replaceLead(res.data.lead);
      res.data.results.forEach((r) => (r.ok ? toast.success(r.message) : toast.error(`${r.provider}: ${r.message}`)));
      loadLeads();
    } catch (err) {
      fail(err, "Enrichment failed.");
    } finally {
      setBusy("");
    }
  };

  const logActivity = async (lead, activity) => {
    try {
      const res = await axios.post(`${API}/leads/${lead.id}/activities`, activity, auth);
      replaceLead(res.data);
      loadLeads();
      return true;
    } catch (err) {
      fail(err, "Could not log activity.");
      return false;
    }
  };

  const removeLead = async (lead) => {
    if (!window.confirm(`Delete ${lead.company}? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/leads/${lead.id}`, auth);
      setSelected(null);
      toast.success(`${lead.company} deleted.`);
      loadLeads();
    } catch (err) {
      fail(err, "Could not delete.");
    }
  };

  const enabledSources = providers?.sources.filter((s) => s.enabled) || [];
  const offProviders = providers ? [...providers.sources, ...providers.enrichers].filter((p) => !p.enabled) : [];

  return (
    <div data-testid="crm-panel">
      {/* Pipeline */}
      <div className="grid grid-cols-3 gap-px border border-white/10 bg-white/10 sm:grid-cols-6" data-testid="crm-pipeline">
        {STATUSES.map((s) => (
          <button key={s.key} onClick={() => setFilters((f) => ({ ...f, status: f.status === s.key ? "" : s.key }))}
            className={`bg-vortex-ink px-4 py-4 text-left transition-colors hover:bg-vortex-surface ${filters.status === s.key ? "bg-vortex-surface" : ""}`}>
            <p className={`font-mono text-[10px] tracking-[0.2em] uppercase ${filters.status === s.key ? "text-vortex-cyan" : "text-slate-500"}`}>{s.label}</p>
            <p className="mt-1 font-heading text-2xl font-bold text-white">{summary?.by_status?.[s.key] ?? "–"}</p>
          </button>
        ))}
      </div>

      {/* Sweep my area: the whole service area, one place at a time */}
      <LeadSweep auth={auth} fail={fail} categories={categories} onAdded={loadLeads} />

      <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
        {/* Today */}
        <div className={`${card} p-6 lg:col-span-5`} data-testid="crm-today">
          <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-vortex-cyan uppercase"><Flame size={13} /> Today's next steps</p>
          {!summary ? (
            <p className="mt-6 text-sm text-slate-500">Loading...</p>
          ) : summary.today.length === 0 ? (
            <p className="mt-6 text-sm text-slate-500">Nothing urgent. Find new leads on the right to fill the pipeline.</p>
          ) : (
            <ul className="mt-4 divide-y divide-white/5">
              {summary.today.map((t) => (
                <li key={t.id}>
                  <button onClick={() => openLead(t.id)} className="flex w-full items-start gap-3 py-3 text-left transition-colors hover:text-vortex-cyan">
                    <Pill className={PRIORITY_STYLE[t.priority]}>{t.priority}</Pill>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-100">{t.company}</span>
                      <span className="block text-xs text-slate-400">{t.step.title}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Find leads */}
        <form onSubmit={runSearch} className={`${card} p-6 lg:col-span-7`} data-testid="crm-search-form">
          <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] text-vortex-cyan uppercase"><Search size={13} /> Find leads</p>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className={label} htmlFor="crm-cat">Business type</Label>
              <select id="crm-cat" className={select} value={search.category} onChange={(e) => setSearch({ ...search, category: e.target.value })}>
                {categories.filter((c) => c.key !== "other").map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                <option value="">Any (use keywords)</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label className={label} htmlFor="crm-loc">Town</Label>
              <Input id="crm-loc" list="crm-towns" className={field} value={search.location} onChange={(e) => setSearch({ ...search, location: e.target.value })} placeholder="Exton, PA" />
              <datalist id="crm-towns">{AREAS.map((a) => <option key={a.slug} value={`${a.name}, PA`} />)}</datalist>
            </div>
            <div className="space-y-2">
              <Label className={label} htmlFor="crm-q">Keywords (optional)</Label>
              <Input id="crm-q" className={field} value={search.query} onChange={(e) => setSearch({ ...search, query: e.target.value })} placeholder="e.g. dental offices, logistics" />
            </div>
            <div className="space-y-2">
              <Label className={label} htmlFor="crm-src">Source</Label>
              <select id="crm-src" className={select} value={search.source} onChange={(e) => setSearch({ ...search, source: e.target.value })}>
                {(providers?.sources || []).map((s) => <option key={s.key} value={s.key} disabled={!s.enabled}>{s.label}{s.enabled ? "" : " (not set up)"}</option>)}
              </select>
            </div>
            <div className="space-y-2 sm:col-span-2" data-testid="crm-radius">
              <Label className={label} htmlFor="crm-radius">Search radius around the town</Label>
              <div className="flex flex-wrap items-center gap-2">
                {[1, 5, 10, 25, 50].map((m) => (
                  <button type="button" key={m} onClick={() => setSearch({ ...search, radius_miles: m })}
                    className={`rounded-full border px-3 py-1.5 font-mono text-[11px] tracking-[0.1em] ${Number(search.radius_miles) === m ? "border-vortex-cyan bg-vortex-cyan text-vortex-ink" : "border-white/15 text-slate-300 hover:border-vortex-cyan/60"}`}>
                    {m} mi
                  </button>
                ))}
                <Input id="crm-radius" type="number" min={1} max={50} step={1} className={`${field} w-20`} value={search.radius_miles}
                  onChange={(e) => setSearch({ ...search, radius_miles: e.target.value })}
                  onBlur={() => setSearch((s) => ({ ...s, radius_miles: Math.min(50, Math.max(1, Math.round(Number(s.radius_miles) || 10))) }))} />
                <span className="text-xs text-slate-500">miles</span>
              </div>
            </div>
            <div className="space-y-2 sm:col-span-2" data-testid="crm-open-24h">
              <label className="flex flex-wrap items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" className="accent-[#5CB3E2]" checked={!!search.open_24h}
                  onChange={(e) => {
                    const on = e.target.checked;
                    const google = (providers?.sources || []).find((s) => s.key === "google_places" && s.enabled);
                    setSearch({ ...search, open_24h: on, ...(on ? { category: "", source: google ? google.key : search.source } : {}) });
                  }} />
                <Clock size={14} className="text-vortex-cyan" /> Only places open 24/7 with no food within
                <select aria-label="No food within" className={`${selectBase} w-24`} value={search.no_food_miles ?? 1}
                  onChange={(e) => setSearch({ ...search, no_food_miles: Number(e.target.value) })}>
                  {[0.5, 1, 2, 5].map((m) => <option key={m} value={m}>{m} mi</option>)}
                </select>
              </label>
              {search.open_24h && (
                <p className="text-xs text-slate-500">
                  Uses Google Places. With "Any" business type it looks for hospitals, nursing homes, warehouses, plants,
                  hotels and other round-the-clock sites, then checks each 24/7 one for restaurants, cafes and
                  convenience stores nearby (one extra Google lookup per place, remembered for 30 days).
                </p>
              )}
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <button type="submit" className={btnPrimary} disabled={searching || !enabledSources.length} data-testid="crm-search-button">
              <Search size={14} /> {searching ? "Searching..." : "Search"}
            </button>
            <button type="button" className={btnGhost} onClick={() => setAddOpen(true)}><Plus size={14} /> Add manually</button>
            <button type="button" className={btnGhost} onClick={() => setCsvOpen(true)}><Upload size={14} /> Import CSV</button>
          </div>
          {lastSearch && !results && (
            <button type="button" onClick={restoreLastSearch} className="mt-4 text-xs text-vortex-cyan hover:underline" data-testid="crm-restore-search">
              Reopen your last search ({lastSearch.results.length} businesses, {lastSearch.around}) without using a new search
            </button>
          )}
        </form>
      </div>

      {results && (
        <div className={`${card} mt-8`} data-testid="crm-search-results">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-6 py-4">
            <p className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">
              {results.length} businesses within {searchedAround ? `${Number(search.radius_miles)} mi of ${searchedAround}` : "your search"} · click one for details{hiddenSkipped > 0 ? ` · ${hiddenSkipped} hidden` : ""}
              {foodSkipped && ` · skipped ${foodSkipped.food_nearby} with food within ${search.no_food_miles ?? 1} mi and ${foodSkipped.not_24h} not open 24/7`}
            </p>
            <div className="flex gap-3">
              <button className={btnGhost} onClick={() => setResults(null)}>Close</button>
              <button className={btnPrimary} onClick={importPicked} disabled={busy === "import" || !Object.values(picked).some(Boolean)}>
                <Plus size={14} /> Add {Object.values(picked).filter(Boolean).length} to CRM
              </button>
            </div>
          </div>
          {results.map((r) => {
            const open = openResult === r.source_ref;
            const d = details[r.source_ref];
            const added = !!r.existing_id;
            return (
              <div key={r.source_ref} className={`border-b border-white/5 ${open ? "bg-vortex-surface/40" : ""}`} data-testid="crm-result">
                <div className="flex items-start gap-4 px-6 py-4">
                  <input type="checkbox" aria-label={`Select ${r.company}`} className="mt-1 accent-[#5CB3E2]" disabled={added}
                    checked={!!picked[r.source_ref]} onChange={(e) => setPicked({ ...picked, [r.source_ref]: e.target.checked })} />
                  <button type="button" onClick={() => toggleResult(r)} className={`min-w-0 flex-1 text-left ${added ? "opacity-60" : ""}`}>
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium text-slate-100 hover:text-vortex-cyan">{r.company}</span>
                      <PotentialPill potential={r.potential} />
                      <Pill className={PRIORITY_STYLE[r.priority]}>{r.priority}</Pill>
                      <Pill className="border-vortex-cyan/40 text-vortex-cyan">{catLabel(r.category)}</Pill>
                      {added && <Pill className="border-slate-500/50 text-slate-400">In your leads</Pill>}
                      {!r.in_service_area && <Pill className="border-slate-600/50 text-slate-500">Outside area</Pill>}
                    </span>
                    <span className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                      {r.hours_per_week === 168 && <span className="flex items-center gap-1 text-emerald-300"><Clock size={11} />Open 24/7</span>}
                      {r.type_label && <span>{r.type_label}</span>}
                      {r.distance_miles != null && <span>{r.distance_miles.toFixed(1)} mi away</span>}
                      {r.rating != null && <span className="flex items-center gap-1"><Star size={11} className="text-amber-300" />{r.rating.toFixed(1)}{r.reviews ? ` (${r.reviews} reviews)` : ""}</span>}
                      <span>{r.address || "No address listed"}</span>
                    </span>
                    <span className="mt-1 flex flex-wrap gap-4 text-xs text-slate-500">
                      {r.phone && <span className="flex items-center gap-1"><Phone size={11} />{r.phone}</span>}
                      {r.email && <span className="flex items-center gap-1"><Mail size={11} />{r.email}</span>}
                      {r.website && <span className="flex items-center gap-1"><Globe size={11} />{r.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}</span>}
                    </span>
                  </button>
                  {open ? <ChevronUp size={16} className="mt-1 text-slate-500" /> : <ChevronDown size={16} className="mt-1 text-slate-500" />}
                </div>
                {open && (
                  <div className="px-6 pb-5 pl-14 text-xs text-slate-300" data-testid="crm-result-details">
                    {!d || d.loading ? (
                      <p className="text-slate-500">Looking up details and checking their website for emails and phone numbers...</p>
                    ) : (
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          {d.summary && <p className="text-slate-300">{d.summary}</p>}
                          {d.people?.length > 0 && (
                            <div data-testid="crm-result-people">
                              <p className={label}>People on their website</p>
                              <ul className="mt-1 space-y-1">
                                {d.people.slice(0, 4).map((p) => (
                                  <li key={p.name} className="flex flex-wrap items-center gap-2">
                                    <User size={11} className="text-vortex-cyan" />
                                    <span>{p.name}{p.title ? `, ${p.title}` : ""}</span>
                                    {p.decision_maker && <Pill className="border-emerald-400/50 text-emerald-300">Decision-maker</Pill>}
                                    {p.email && <span className="text-slate-400">{p.email}</span>}
                                    {p.phone && <span className="text-slate-400">{p.phone}</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {(d.emails?.length > 0 || d.phones?.length > 0) ? (
                            <div>
                              <p className={label}>Found on their website</p>
                              <ul className="mt-1 space-y-1">
                                {d.emails?.map((em) => <li key={em} className="flex items-center gap-1"><Mail size={11} className="text-vortex-cyan" />{em}</li>)}
                                {d.phones?.map((ph) => <li key={ph} className="flex items-center gap-1"><Phone size={11} className="text-vortex-cyan" />{ph}</li>)}
                              </ul>
                            </div>
                          ) : <p className="text-slate-500">{r.website ? "No emails or phone numbers found on their website." : "No website listed."}</p>}
                          <div className="flex flex-wrap gap-4">
                            {(d.maps_url || r.maps_url) && <a href={d.maps_url || r.maps_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-vortex-cyan hover:underline"><MapPin size={11} /> Google Maps</a>}
                            {streetViewUrl(r) && <a href={streetViewUrl(r)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-vortex-cyan hover:underline" data-testid="crm-streetview"><Eye size={11} /> Street View</a>}
                            {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-vortex-cyan hover:underline"><ExternalLink size={11} /> Website</a>}
                          </div>
                          {d.errors?.map((er) => <p key={er} className="text-amber-300">{er}</p>)}
                        </div>
                        {d.hours?.length > 0 && (
                          <div>
                            <p className={label}>Hours</p>
                            <ul className="mt-1 space-y-0.5 text-slate-400">{d.hours.map((h) => <li key={h}>{h}</li>)}</ul>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      {!added && (
                        <button type="button" className={btnPrimary} onClick={() => importOne(r)} disabled={busy === `import-${r.source_ref}`}>
                          <Plus size={14} /> Add to my leads
                        </button>
                      )}
                      <span className="flex items-center gap-2 text-slate-500" data-testid="crm-hide-result">
                        <EyeOff size={12} /> Hide:
                        {HIDE_REASONS.map((h) => (
                          <button key={h.key} type="button" className="text-slate-400 underline-offset-2 hover:text-red-300 hover:underline"
                            onClick={() => hideResult(r, h.key)} disabled={busy === `hide-${r.source_ref}`}>{h.label}</button>
                        ))}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {cursor && (
            <div className="px-6 py-4">
              <button className={btnGhost} onClick={(e) => runSearch(e, true)} disabled={searching} data-testid="crm-load-more">
                <RefreshCw size={13} /> {searching ? "Loading..." : "Load more results"}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Leads table */}
      <div className="mt-14" data-testid="crm-leads">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-mono text-xs tracking-[0.25em] text-vortex-cyan uppercase">Pipeline</p>
            <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-white">Leads</h2>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Input className={`${field} w-56`} placeholder="Search name, email, town" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
            <select className={`${selectBase} w-48`} value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
              <option value="">All types</option>
              {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
            <select className={`${selectBase} w-48`} value={filters.sort} onChange={(e) => setFilters({ ...filters, sort: e.target.value })} aria-label="Sort leads" data-testid="crm-sort">
              <option value="potential">Best vending potential</option>
              <option value="grade">Best A/B/C grade</option>
            </select>
            <button className={`${btnGhost} ${filters.hidden ? "border-vortex-cyan/60 text-vortex-cyan" : ""}`} data-testid="crm-show-hidden"
              onClick={() => setFilters({ ...filters, hidden: filters.hidden ? "" : "true" })}>
              <EyeOff size={13} /> {filters.hidden ? "Showing hidden" : `Hidden${summary?.hidden ? ` (${summary.hidden})` : ""}`}
            </button>
            <button className={btnGhost} onClick={loadLeads} aria-label="Refresh leads"><RefreshCw size={14} /></button>
          </div>
        </div>
        <div className="mt-6 border border-white/10">
          <div className="hidden grid-cols-12 gap-4 border-b border-white/10 bg-vortex-surface/60 px-6 py-3 font-mono text-[10px] tracking-[0.25em] text-slate-500 uppercase md:grid">
            <span className="col-span-4">Company</span>
            <span className="col-span-3">Contact</span>
            <span className="col-span-1">Status</span>
            <span className="col-span-4">Next step</span>
          </div>
          {loading ? (
            <p className="px-6 py-10 text-sm text-slate-500">Loading leads...</p>
          ) : leads.length === 0 ? (
            <p className="px-6 py-10 text-sm text-slate-500" data-testid="crm-empty">
              {filters.hidden ? "No hidden leads." : filters.status || filters.category || filters.q ? "No leads match these filters." : "No leads yet. Search for businesses above, add one manually, or import a CSV. Contact form enquiries land here automatically."}
            </p>
          ) : (
            leads.map((l) => (
              <button key={l.id} onClick={() => setSelected(l)} data-testid={`crm-lead-row-${l.id}`}
                className="grid w-full grid-cols-1 gap-2 border-b border-white/5 px-6 py-4 text-left transition-colors hover:bg-vortex-surface/40 md:grid-cols-12 md:gap-4">
                <span className="col-span-4 min-w-0">
                  <span className="flex items-center gap-2">
                    <Pill className={PRIORITY_STYLE[l.priority]}>{l.priority}</Pill>
                    <span className="truncate text-sm font-medium text-slate-100">{l.company}</span>
                  </span>
                  <span className="mt-1 block"><PotentialPill potential={l.potential} /></span>
                  <span className="mt-1 block truncate text-xs text-slate-500">{catLabel(l.category)}{l.city ? ` · ${l.city}` : ""}</span>
                </span>
                <span className="col-span-3 min-w-0 text-xs text-slate-400">
                  {l.contact_name && <span className="block truncate text-slate-300">{l.contact_name}{l.title ? `, ${l.title}` : ""}</span>}
                  {l.contact_phone && <span className="block">{l.contact_phone} (direct)</span>}
                  {l.phone && <span className="block">{l.phone}</span>}
                  {l.email && <span className="block truncate">{l.email}</span>}
                  {!l.phone && !l.email && <span className="text-slate-600">No contact info yet</span>}
                </span>
                <span className="col-span-1"><Pill className="border-white/15 text-slate-300">{statusLabel(l.status)}</Pill></span>
                <span className="col-span-4 text-xs">
                  {l.next_steps[0] ? (
                    <span className={URGENCY_STYLE[l.next_steps[0].urgency]}>{l.next_steps[0].title}</span>
                  ) : <span className="text-slate-600">—</span>}
                </span>
              </button>
            ))
          )}
        </div>
        <p className="mt-4 font-mono text-[10px] tracking-[0.2em] text-slate-600 uppercase">{leads.length} lead{leads.length === 1 ? "" : "s"} shown · {summary?.total ?? 0} total</p>
      </div>

      {offProviders.length > 0 && (
        <div className="mt-10 border border-dashed border-white/10 p-6 text-xs text-slate-500" data-testid="crm-providers-off">
          <p className="font-mono text-[10px] tracking-[0.25em] text-slate-400 uppercase">Data providers not set up</p>
          <ul className="mt-3 space-y-1.5">
            {offProviders.map((p) => (
              <li key={p.key}>
                <span className="text-slate-300">{p.label}</span>: {p.note} Set <code className="text-vortex-cyan">{p.env_key}</code> on the API server to turn it on.{" "}
                {p.signup_url && <a href={p.signup_url} target="_blank" rel="noreferrer" className="text-vortex-cyan hover:underline">Get a key</a>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <LeadSheet lead={selected} onClose={() => setSelected(null)} categories={categories} onPatch={patchLead}
        onEnrich={enrich} enriching={selected && busy === `enrich-${selected.id}`} onLog={logActivity} onDelete={removeLead} />

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-vortex-surface text-slate-100 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-white">Add a lead</DialogTitle>
            <DialogDescription className="text-sm text-slate-400">Type is guessed from the name if you leave it blank.</DialogDescription>
          </DialogHeader>
          <form onSubmit={addLead} className="grid grid-cols-2 gap-4">
            {[["company", "Company *", 2], ["contact_name", "Contact name", 1], ["title", "Title", 1], ["phone", "Phone", 1], ["email", "Email", 1],
              ["website", "Website", 2], ["address", "Street address", 2], ["city", "Town", 1], ["postal_code", "ZIP", 1]].map(([k, l, span]) => (
              <div key={k} className={`space-y-1.5 ${span === 2 ? "col-span-2" : ""}`}>
                <Label className={label} htmlFor={`new-${k}`}>{l}</Label>
                <Input id={`new-${k}`} className={field} required={k === "company"} value={newLead[k]} onChange={(e) => setNewLead({ ...newLead, [k]: e.target.value })} />
              </div>
            ))}
            <div className="col-span-2 space-y-1.5">
              <Label className={label} htmlFor="new-cat">Business type</Label>
              <select id="new-cat" className={select} value={newLead.category} onChange={(e) => setNewLead({ ...newLead, category: e.target.value })}>
                <option value="">Guess for me</option>
                {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label className={label} htmlFor="new-notes">Notes</Label>
              <Textarea id="new-notes" className={field} value={newLead.notes} onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })} />
            </div>
            <button type="submit" className={`${btnPrimary} col-span-2`} disabled={busy === "add"}><Plus size={14} /> {busy === "add" ? "Adding..." : "Add lead"}</button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
        <DialogContent className="border-white/10 bg-vortex-surface text-slate-100 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold text-white">Import leads from CSV</DialogTitle>
            <DialogDescription className="text-sm text-slate-400">
              Needs a header row. Recognized columns: Company, Contact, Title, Email, Phone, Website, Address, City, State, ZIP, Notes. Duplicates are skipped.
            </DialogDescription>
          </DialogHeader>
          <input type="file" accept=".csv,text/csv" onChange={readCsvFile} className="text-xs text-slate-400 file:mr-3 file:rounded-full file:border-0 file:bg-vortex-cyan file:px-4 file:py-2 file:font-mono file:text-[10px] file:uppercase file:tracking-[0.2em] file:text-vortex-ink" />
          <Textarea className={`${field} h-40 font-mono text-xs`} value={csvText} onChange={(e) => setCsvText(e.target.value)} placeholder={"Company,Phone,Website,City\nAcme Corp,610-555-0100,acme.com,Exton"} />
          <button className={btnPrimary} onClick={importCsv} disabled={!csvText.trim() || busy === "csv"}><Upload size={14} /> {busy === "csv" ? "Importing..." : "Import"}</button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const LeadSheet = ({ lead, onClose, categories, onPatch, onEnrich, enriching, onLog, onDelete }) => {
  const [draft, setDraft] = useState(null);
  const [activity, setActivity] = useState({ type: "call", text: "", next_follow_up: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (lead) setDraft({
      company: lead.company, contact_name: lead.contact_name || "", title: lead.title || "", email: lead.email || "",
      contact_phone: lead.contact_phone || "",
      phone: lead.phone || "", website: lead.website || "", address: lead.address || "", city: lead.city || "",
      postal_code: lead.postal_code || "", notes: lead.notes || "",
      next_follow_up: lead.next_follow_up ? lead.next_follow_up.slice(0, 10) : "",
    });
  }, [lead]);

  if (!lead || !draft) return <Sheet open={false} />;

  const dirty = Object.entries(draft).some(([k, v]) => (k === "next_follow_up" ? (lead.next_follow_up || "").slice(0, 10) : lead[k] || "") !== v);

  const save = async () => {
    setSaving(true);
    await onPatch(lead.id, { ...draft, next_follow_up: draft.next_follow_up || null });
    setSaving(false);
  };

  const submitActivity = async (e) => {
    e.preventDefault();
    const ok = await onLog(lead, { type: activity.type, text: activity.text, next_follow_up: activity.next_follow_up || null });
    if (ok) setActivity({ type: activity.type, text: "", next_follow_up: "" });
  };

  const applyFound = (key, value) => onPatch(lead.id, { [key]: value });

  return (
    <Sheet open={!!lead} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto border-white/10 bg-vortex-surface text-slate-100 sm:max-w-xl" data-testid="crm-lead-sheet">
        <SheetHeader>
          <SheetTitle className="pr-6 font-heading text-2xl font-bold text-white">{lead.company}</SheetTitle>
          <SheetDescription asChild>
            <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
              <PotentialPill potential={lead.potential} long />
              <Pill className={PRIORITY_STYLE[lead.priority]}>Priority {lead.priority} · {lead.score}</Pill>
              {lead.in_service_area ? <Pill className="border-emerald-400/40 text-emerald-300">In service area</Pill> : <Pill className="border-slate-600/50 text-slate-500">Outside service area</Pill>}
              <span>Source: {lead.source.replace(/_/g, " ")}</span>
            </div>
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <select className={select} value={lead.status} onChange={(e) => onPatch(lead.id, { status: e.target.value })} aria-label="Status" data-testid="crm-status-select">
            {STATUSES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select className={select} value={lead.category} onChange={(e) => onPatch(lead.id, { category: e.target.value })} aria-label="Business type">
            {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>

        <PotentialPanel lead={lead} onPatch={onPatch} onCheckSite={() => onEnrich(lead, ["site"])} checking={enriching} />

        {/* Next steps */}
        <div className="mt-6">
          <p className={label}>Next steps</p>
          {lead.next_steps.length === 0 ? <p className="mt-2 text-sm text-slate-500">Nothing to do right now.</p> : (
            <ul className="mt-2 space-y-2">
              {lead.next_steps.map((s) => (
                <li key={s.key} className="border border-white/10 bg-vortex-ink/40 p-3">
                  <p className={`text-sm font-medium ${URGENCY_STYLE[s.urgency]}`}>{s.title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-slate-400">{s.detail}</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {s.action === "call" && lead.phone && <a href={`tel:${lead.phone.replace(/[^\d+]/g, "")}`} className={btnGhost}><Phone size={12} /> Call</a>}
                    {s.action === "call_direct" && lead.contact_phone && <a href={`tel:${lead.contact_phone.replace(/[^\d+]/g, "")}`} className={btnGhost}><Phone size={12} /> Call {lead.contact_name}</a>}
                    {s.action === "email" && lead.email && <a href={`mailto:${lead.email}`} className={btnGhost}><Mail size={12} /> Email</a>}
                    {s.action === "enrich" && <button className={btnGhost} onClick={() => onEnrich(lead)} disabled={enriching}><Sparkles size={12} /> {enriching ? "Enriching..." : "Enrich"}</button>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Details */}
        <div className="mt-8 flex items-center justify-between">
          <p className={label}>Details</p>
          <button className={btnGhost} onClick={() => onEnrich(lead)} disabled={enriching} data-testid="crm-enrich-button">
            <Sparkles size={12} /> {enriching ? "Searching..." : "Find decision-maker"}
          </button>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          {[["company", "Company", 2], ["contact_name", "Decision-maker", 1], ["title", "Title", 1], ["contact_phone", "Direct phone", 1],
            ["phone", "Main phone", 1], ["email", "Email", 2],
            ["website", "Website", 2], ["address", "Address", 2], ["city", "Town", 1], ["postal_code", "ZIP", 1]].map(([k, l, span]) => (
            <div key={k} className={`space-y-1 ${span === 2 ? "col-span-2" : ""}`}>
              <Label className={label} htmlFor={`lead-${k}`}>{l}</Label>
              <Input id={`lead-${k}`} className={field} value={draft[k]} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })} />
            </div>
          ))}
          <div className="col-span-2 space-y-1">
            <Label className={label} htmlFor="lead-notes">Notes</Label>
            <Textarea id="lead-notes" className={field} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label className={label} htmlFor="lead-fu">Follow up on</Label>
            <Input id="lead-fu" type="date" className={field} value={draft.next_follow_up} onChange={(e) => setDraft({ ...draft, next_follow_up: e.target.value })} />
          </div>
          <div className="flex items-end">
            <button className={`${btnPrimary} w-full`} onClick={save} disabled={!dirty || saving}>{saving ? "Saving..." : "Save changes"}</button>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-xs">
          {lead.website && <a href={lead.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-vortex-cyan hover:underline"><ExternalLink size={11} /> Website</a>}
          {lead.address && <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${lead.company} ${lead.address}`)}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-vortex-cyan hover:underline"><MapPin size={11} /> Map</a>}
          {(lead.lat != null || lead.address) && <a href={streetViewUrl(lead)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-vortex-cyan hover:underline"><Eye size={11} /> Street View</a>}
        </div>

        {/* Enrichment finds */}
        {(lead.contacts.length > 0 || lead.emails_found.length > 0 || lead.phones_found.length > 0) && (
          <div className="mt-8" data-testid="crm-found">
            <p className={label}>Found by enrichment</p>
            <ul className="mt-2 space-y-1.5 text-xs text-slate-300">
              {lead.contacts.map((c, i) => (
                <li key={`c${i}`} className="flex flex-wrap items-center gap-2">
                  <User size={11} className="text-vortex-cyan" />
                  <span>{c.name || "Unnamed"}{c.title ? `, ${c.title}` : ""}</span>
                  {c.decision_maker && <Pill className="border-emerald-400/50 text-emerald-300">Decision-maker</Pill>}
                  {c.email && <span className="text-slate-400">{c.email}{c.confidence ? ` (${c.confidence}%)` : ""}</span>}
                  {c.phone && <a href={`tel:${c.phone.replace(/[^\d+]/g, "")}`} className="text-slate-400 hover:text-vortex-cyan">{c.phone}</a>}
                  {c.name && c.name !== lead.contact_name && (
                    <button className="text-vortex-cyan hover:underline" onClick={() => onPatch(lead.id, { contact_name: c.name, title: c.title || "", email: c.email || lead.email, contact_phone: c.phone || "" })}>Use</button>
                  )}
                </li>
              ))}
              {lead.emails_found.filter((e) => !lead.contacts.some((c) => c.email === e)).map((e) => (
                <li key={e} className="flex items-center gap-2"><Mail size={11} className="text-vortex-cyan" />{e}
                  {e !== lead.email && <button className="text-vortex-cyan hover:underline" onClick={() => applyFound("email", e)}>Use</button>}</li>
              ))}
              {lead.phones_found.map((p) => (
                <li key={p} className="flex items-center gap-2"><Phone size={11} className="text-vortex-cyan" />{p}
                  {p !== lead.phone && <button className="text-vortex-cyan hover:underline" onClick={() => applyFound("phone", p)}>Use</button>}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Activity */}
        <form onSubmit={submitActivity} className="mt-8 space-y-3">
          <p className={label}>Log activity</p>
          <div className="flex flex-wrap gap-2">
            {["call", "email", "meeting", "visit", "note"].map((t) => (
              <button type="button" key={t} onClick={() => setActivity({ ...activity, type: t })}
                className={`rounded-full border px-3 py-1 font-mono text-[10px] tracking-[0.15em] uppercase ${activity.type === t ? "border-vortex-cyan text-vortex-cyan" : "border-white/15 text-slate-400"}`}>{t}</button>
            ))}
          </div>
          <Textarea className={field} placeholder="What happened? e.g. Spoke with front desk, ask for Dana on Tuesdays" value={activity.text} onChange={(e) => setActivity({ ...activity, text: e.target.value })} />
          <div className="flex items-end gap-3">
            <div className="flex-1 space-y-1">
              <Label className={label} htmlFor="act-fu">Next follow-up (optional)</Label>
              <Input id="act-fu" type="date" className={field} value={activity.next_follow_up} onChange={(e) => setActivity({ ...activity, next_follow_up: e.target.value })} />
            </div>
            <button type="submit" className={btnPrimary} data-testid="crm-log-button">Log</button>
          </div>
        </form>
        <ul className="mt-6 space-y-3 border-l border-white/10 pl-4" data-testid="crm-activity">
          {[...lead.activities].reverse().map((a) => (
            <li key={a.id} className="text-xs">
              <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] text-slate-500 uppercase">
                {a.type === "status" ? <CheckCircle2 size={11} /> : <Circle size={9} />}{a.type} · {new Date(a.at).toLocaleString()}
              </p>
              {a.text && <p className="mt-1 whitespace-pre-wrap text-slate-300">{a.text}</p>}
            </li>
          ))}
          {lead.activities.length === 0 && <li className="text-xs text-slate-500">No activity yet.</li>}
        </ul>

        <div className="mt-10 flex flex-wrap items-center gap-3 text-xs" data-testid="crm-hide-lead">
          {lead.hidden ? (
            <>
              <span className="text-slate-400">Hidden from lists and searches{lead.hidden_reason === "rejected" ? " (turned us down)" : lead.hidden_reason === "low_quality" ? " (poor location)" : ""}.</span>
              <button className={btnGhost} onClick={() => onPatch(lead.id, { hidden: false })}><Eye size={12} /> Unhide</button>
            </>
          ) : (
            <>
              <span className="flex items-center gap-1 text-slate-500"><EyeOff size={12} /> Hide this lead:</span>
              {HIDE_REASONS.map((h) => (
                <button key={h.key} className={btnGhost} onClick={() => onPatch(lead.id, { hidden: true, hidden_reason: h.key })}>{h.label}</button>
              ))}
            </>
          )}
        </div>

        <button onClick={() => onDelete(lead)} className="mt-6 flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase transition-colors hover:text-red-400">
          <Trash2 size={12} /> Delete lead
        </button>
      </SheetContent>
    </Sheet>
  );
};

// Vending potential: the score's reasons, plus what Dan learns on a visit
const PotentialPanel = ({ lead, onPatch, onCheckSite, checking }) => {
  const p = lead.potential;
  const apartment = lead.category === "apartment";
  const [nums, setNums] = useState({});
  const [override, setOverride] = useState("");
  useEffect(() => {
    setNums({ units: lead.units ?? "", employees: lead.employees ?? "", parking_spaces: lead.parking_spaces ?? "" });
    setOverride(lead.potential_override ?? "");
  }, [lead]);
  if (!p) return null;
  const saveNum = (key) => {
    const raw = String(nums[key]).trim();
    const value = raw === "" ? null : Math.max(0, parseInt(raw, 10) || 0);
    if (value !== (lead[key] ?? null)) onPatch(lead.id, { [key]: value });
  };
  const site = lead.site || {};
  const numInput = (key, text) => (
    <div className="space-y-1">
      <Label className={label} htmlFor={`pot-${key}`}>{text}</Label>
      <Input id={`pot-${key}`} type="number" min="0" inputMode="numeric" className={field} value={nums[key]} placeholder="Unknown"
        onChange={(e) => setNums({ ...nums, [key]: e.target.value })} onBlur={() => saveNum(key)}
        onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} />
    </div>
  );
  return (
    <div className="mt-6 border border-white/10 bg-vortex-ink/40 p-4" data-testid="crm-potential-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={label}>Vending potential</p>
        <button className={btnGhost} onClick={onCheckSite} disabled={checking} data-testid="crm-check-site">
          <Building2 size={12} /> {checking ? "Checking..." : "Check building & parking"}
        </button>
      </div>
      <p className="mt-2 font-heading text-3xl font-bold text-white">{p.score}<span className="ml-2 text-sm font-normal capitalize text-slate-400">{p.level}{p.overridden ? " (set by you)" : ""}</span></p>
      <ul className="mt-3 space-y-2">
        {p.parts.map((part) => (
          <li key={part.key} className="text-xs">
            <div className="flex justify-between gap-3">
              <span className="text-slate-200">{part.label}</span>
              <span className="font-mono text-slate-400">{part.max ? `${part.points}/${part.max}` : `${part.points > 0 ? "+" : ""}${part.points}`}</span>
            </div>
            {part.max > 0 && (
              <div className="mt-1 h-1 rounded-full bg-white/10"><div className="h-1 rounded-full bg-vortex-cyan" style={{ width: `${(part.points / part.max) * 100}%` }} /></div>
            )}
            <p className="mt-0.5 text-slate-500">{part.why}</p>
          </li>
        ))}
      </ul>

      {apartment && (
        <div className="mt-4">
          <p className={label}>Building style</p>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="crm-building-style">
            {BUILDING_STYLES.map((b) => {
              const on = (lead.building_style || "") === b.key;
              const guessed = !lead.building_style && p.style === b.key;
              return (
                <button key={b.key} type="button" onClick={() => onPatch(lead.id, { building_style: on ? "" : b.key })}
                  className={`rounded-full border px-3 py-1 text-xs ${on ? "border-vortex-cyan bg-vortex-cyan/10 text-vortex-cyan" : guessed ? "border-dashed border-vortex-cyan/60 text-slate-200" : "border-white/15 text-slate-400"}`}>
                  {b.label} <span className="text-slate-500">{b.hint}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-1 text-[11px] text-slate-500">
            {lead.building_style ? "Your pick. Tap it again to go back to the automatic guess." : p.style ? `Guessed from ${p.style_source}. Check Street View and tap the right one.` : "Not known yet. Check Street View and tap one."}
          </p>
        </div>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3">
        {apartment ? numInput("units", "Units") : numInput("employees", "People on site")}
        {numInput("parking_spaces", "Parking spaces")}
      </div>
      {lead.parking_source === "osm" && <p className="mt-1 text-[11px] text-slate-500">Parking estimated from the map's lot outlines. Type the real count if you know it.</p>}

      <p className={`${label} mt-4`}>From your visit</p>
      <div className="mt-2 grid gap-1.5 sm:grid-cols-2">
        {SITE_CHECKS.map((c) => (
          <label key={c.key} className="flex items-center gap-2 text-xs text-slate-300">
            <input type="checkbox" className="accent-[#5CB3E2]" checked={!!site[c.key]}
              onChange={(e) => onPatch(lead.id, { site: { ...site, [c.key]: e.target.checked } })} />
            {c.label}
          </label>
        ))}
      </div>

      <div className="mt-4 flex items-end gap-3">
        <div className="flex-1 space-y-1">
          <Label className={label} htmlFor="pot-override">Your own score (optional)</Label>
          <Input id="pot-override" type="number" min="0" max="100" className={field} value={override} placeholder="Use the calculated score"
            onChange={(e) => setOverride(e.target.value)} />
        </div>
        <button className={btnGhost} onClick={() => {
          const raw = String(override).trim();
          onPatch(lead.id, { potential_override: raw === "" ? null : Math.min(100, Math.max(0, parseInt(raw, 10) || 0)) });
        }}>Set</button>
      </div>
    </div>
  );
};

export default CrmPanel;
